import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  Text,
  Spinner,
  Select,
  Grid,
  Badge,
  ProgressBar
} from '@shopify/polaris'

import { useAuth } from '@/contexts/AuthContext'
import { storeApi, analyticsApi } from '@/services/api'
import type { AnalyticsPeriod } from '@/types'

export default function DashboardPage() {
  const { user } = useAuth()
  const [period, setPeriod] = useState<AnalyticsPeriod>('7d')

  const { data: storeData, isLoading: storeLoading } = useQuery({
    queryKey: ['store', user?.storeId],
    queryFn: () => storeApi.getStore(user!.storeId),
    enabled: !!user?.storeId
  })

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics', user?.storeId, period],
    queryFn: () => analyticsApi.getDashboard(user!.storeId, period),
    enabled: !!user?.storeId
  })

  const periodOptions = [
    { label: 'Last 24 hours', value: '1d' },
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' }
  ]

  if (storeLoading || analyticsLoading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading dashboard" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  const store = storeData?.data.data.store
  const analytics = analyticsData?.data.data

  const getConversationStats = () => {
    if (!analytics?.metrics.conversations) return { total: 0, new: 0, inProgress: 0, resolved: 0 }
    
    const stats = analytics.metrics.conversations.reduce((acc, item) => {
      acc.total += item._count.id
      if (item.status === 'NEW') acc.new = item._count.id
      if (item.status === 'IN_PROGRESS') acc.inProgress = item._count.id
      if (item.status === 'RESOLVED') acc.resolved = item._count.id
      return acc
    }, { total: 0, new: 0, inProgress: 0, resolved: 0 })
    
    return stats
  }

  const getMessageStats = () => {
    if (!analytics?.metrics.messages) return { inbound: 0, outbound: 0, total: 0 }
    
    const stats = analytics.metrics.messages.reduce((acc, item) => {
      if (item.direction === 'INBOUND') acc.inbound = item._count.id
      if (item.direction === 'OUTBOUND') acc.outbound = item._count.id
      acc.total += item._count.id
      return acc
    }, { inbound: 0, outbound: 0, total: 0 })
    
    return stats
  }

  const conversationStats = getConversationStats()
  const messageStats = getMessageStats()
  const responseTime = analytics?.metrics.avgResponseTime || 0

  return (
    <Page 
      title="Dashboard"
      subtitle={`Welcome back, ${user?.name}`}
      primaryAction={{
        content: 'View Conversations',
        url: '/conversations'
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text variant="headingMd" as="h2">
                Overview
              </Text>
              <Select
                label=""
                options={periodOptions}
                value={period}
                onChange={(value) => setPeriod(value as AnalyticsPeriod)}
              />
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {conversationStats.total}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Total Conversations
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="info">{conversationStats.new} New</Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {messageStats.total}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Total Messages
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="success">{messageStats.outbound} Sent</Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {analytics?.metrics.customers || 0}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Active Customers
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="attention">WhatsApp</Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    ${analytics?.metrics.orders.revenue?.toFixed(2) || '0.00'}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Revenue Generated
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="success">{analytics?.metrics.orders.count || 0} Orders</Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 8, xl: 8 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Response Performance
                  </Text>
                  <div style={{ marginTop: '16px' }}>
                    <Text variant="bodyMd" as="p">
                      Average Response Time
                    </Text>
                    <Text variant="headingLg" as="h4">
                      {responseTime > 0 ? `${Math.round(responseTime / 60)} minutes` : 'N/A'}
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      <ProgressBar 
                        progress={responseTime > 0 ? Math.min((300 - responseTime) / 300 * 100, 100) : 0} 
                        size="small" 
                      />
                    </div>
                    <Text variant="bodySm" as="p" color="subdued">
                      Target: Under 5 minutes
                    </Text>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 4, xl: 4 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Store Status
                  </Text>
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <Text variant="bodyMd" as="p">
                        WhatsApp Integration
                      </Text>
                      <Badge status={store?.hasWhatsappIntegration ? 'success' : 'critical'}>
                        {store?.hasWhatsappIntegration ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <Text variant="bodyMd" as="p">
                        Shopify Integration
                      </Text>
                      <Badge status={store?.hasShopifyIntegration ? 'success' : 'critical'}>
                        {store?.hasShopifyIntegration ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>
                    <div>
                      <Text variant="bodyMd" as="p">
                        Store Status
                      </Text>
                      <Badge status={store?.status === 'ACTIVE' ? 'success' : 'critical'}>
                        {store?.status || 'Unknown'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h3">
                Quick Actions
              </Text>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '16px', 
                marginTop: '16px' 
              }}>
                <Card>
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <Text variant="bodyMd" as="p">
                      View active conversations and respond to customers
                    </Text>
                    <div style={{ marginTop: '12px' }}>
                      <a href="/conversations" style={{ textDecoration: 'none' }}>
                        <Badge status="info">Go to Conversations</Badge>
                      </a>
                    </div>
                  </div>
                </Card>
                
                <Card>
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <Text variant="bodyMd" as="p">
                      Manage customer information and history
                    </Text>
                    <div style={{ marginTop: '12px' }}>
                      <a href="/customers" style={{ textDecoration: 'none' }}>
                        <Badge status="success">Manage Customers</Badge>
                      </a>
                    </div>
                  </div>
                </Card>
                
                <Card>
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <Text variant="bodyMd" as="p">
                      View detailed analytics and reports
                    </Text>
                    <div style={{ marginTop: '12px' }}>
                      <a href="/analytics" style={{ textDecoration: 'none' }}>
                        <Badge status="attention">View Analytics</Badge>
                      </a>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}